import React, { useState, useEffect } from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  Badge,
  Divider,
  ListItemIcon,
  ListItemText,
  useTheme,
  alpha,
  Fade,
  Tooltip
} from "@mui/material";
import {
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  Person as PersonIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  AccountCircle as AccountIcon,
  FiberManualRecord as StatusIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ReactComponent as LogoWhite } from "../assets/images/logos/xtremelogowhite.svg";
import DynamicBreadcrumb from "./DynamicBreadcrumb";

// Styled Components
const StyledAppBar = styled(AppBar)`
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border-bottom: 1px solid rgba(13, 110, 253, 0.1) !important;
  box-shadow: 0 4px 20px rgba(13, 110, 253, 0.08) !important;
  position: sticky !important;
  top: 0;
  z-index: 1200;
`;

const SessionIndicator = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: rgba(76, 175, 80, 0.1);
  border-radius: 20px;
  border: 1px solid rgba(76, 175, 80, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(76, 175, 80, 0.15);
    transform: scale(1.02);
  }
`;

const NotificationBadge = styled(Badge)`
  .MuiBadge-badge {
    background: linear-gradient(135deg, #f44336 0%, #d32f2f 100%);
    color: white;
    font-weight: 600;
    font-size: 0.75rem;
    animation: pulse 2s infinite;
  }
  
  @keyframes pulse {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.1);
    }
    100% {
      transform: scale(1);
    }
  }
`;

const UserAvatar = styled(Avatar)`
  width: 40px !important;
  height: 40px !important;
  background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%) !important;
  border: 2px solid rgba(13, 110, 253, 0.2) !important;
  transition: all 0.3s ease !important;
  cursor: pointer !important;
  
  &:hover {
    transform: scale(1.05);
    border-color: #0d6efd !important;
    box-shadow: 0 4px 15px rgba(13, 110, 253, 0.3) !important;
  }
`;

const StyledMenu = styled(Menu)`
  .MuiPaper-root {
    background: rgba(255, 255, 255, 0.95) !important;
    backdrop-filter: blur(20px);
    border-radius: 16px !important;
    border: 1px solid rgba(13, 110, 253, 0.1);
    box-shadow: 0 8px 32px rgba(13, 110, 253, 0.15) !important;
    min-width: 220px;
    margin-top: 8px;
  }
  
  .MuiMenuItem-root {
    border-radius: 8px !important;
    margin: 4px 8px !important;
    transition: all 0.2s ease !important;
    
    &:hover {
      background: rgba(13, 110, 253, 0.08) !important;
      transform: translateX(4px);
    }
  }
`;

const MobileMenuButton = styled(IconButton)`
  background: rgba(13, 110, 253, 0.1) !important;
  color: #0d6efd !important;
  border-radius: 12px !important;
  transition: all 0.3s ease !important;
  
  &:hover {
    background: rgba(13, 110, 253, 0.15) !important;
    transform: scale(1.05);
  }
`;

const Header = ({ onToggleSidebar }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState(null);
  const [notificationAnchor, setNotificationAnchor] = useState(null);
  const [sessionTime, setSessionTime] = useState(0);
  const [notifications] = useState(3); // Mock notification count
  // Session timer
  useEffect(() => {
    const timer = setInterval(() => {
      setSessionTime(prev => prev + 1);
    }, 60000); // Update every minute

    return () => clearInterval(timer);
  }, []);

  // Get user name from localStorage
  const userData = localStorage.getItem("userData");
  const name = userData && JSON.parse(userData).name;

  const logout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/");
  };
  const settingsOpen=() => {
    setAnchorEl(null);
    navigate("/settings");
  };

   const showMobilemenu = () => {
    if (onToggleSidebar) {
      onToggleSidebar(); 
    } else {
     
      document.getElementById("sidebarArea")?.classList.toggle("showSidebar");
    }
  };

  const handleUserMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationOpen = (event) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const formatSessionTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <StyledAppBar position="sticky">
      <Toolbar sx={{ justifyContent: 'space-between', px: { xs: 2, md: 3 } }}>
        {/* Left Section */}
       

        {/* Center Section - Breadcrumb */}
        <Box sx={{ flex: 1, mx: 3, display: { xs: 'none', md: 'block' } }}>
          <DynamicBreadcrumb />
        </Box>
         <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 , mx: 3 }}>
          {/* Session Indicator - Left Side */}
          <Fade in timeout={1000}>
            <SessionIndicator sx={{ display: { xs: 'none', sm: 'flex' } }}>
              <StatusIcon 
                sx={{ 
                  color: '#4caf50', 
                  fontSize: 12,
                  animation: 'pulse 2s infinite'
                }} 
              />
              <Typography variant="caption" sx={{ color: '#4caf50', fontWeight: 600 }}>
                Active {formatSessionTime(sessionTime)}
              </Typography>
            </SessionIndicator>
          </Fade>

          {/* Mobile Menu Button */}
          <MobileMenuButton
            sx={{ display: { lg: 'none' } }}
            onClick={showMobilemenu}
            size="small"
          >
            <MenuIcon />
          </MobileMenuButton>

          {/* Logo for Mobile */}
          <Box sx={{ display: { xs: 'block', lg: 'none' } }}>
            <LogoWhite style={{ height: '32px' }} />
          </Box>
        </Box>

        {/* Right Section */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* User Name - Desktop Only */}
          <Typography 
            variant="body2" 
            sx={{ 
              color: '#1a202c',
              fontWeight: 600,
              display: { xs: 'none', sm: 'block' },
              mr: 1
            }}
          >
            {name || 'User'}
          </Typography>

          {/* Notifications */}
          <Tooltip title="Notifications">
            <IconButton 
              onClick={handleNotificationOpen}
              sx={{ 
                color: '#64748b',
                '&:hover': { 
                  color: '#0d6efd',
                  background: alpha('#0d6efd', 0.08)
                }
              }}
            >
              <NotificationBadge badgeContent={notifications} max={9}>
                <NotificationsIcon />
              </NotificationBadge>
            </IconButton>
          </Tooltip>

          {/* User Avatar */}
          <Tooltip title="Account">
            <UserAvatar onClick={handleUserMenuOpen}>
              <PersonIcon />
            </UserAvatar>
          </Tooltip>
        </Box>

        {/* User Menu */}
        <StyledMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleUserMenuClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          {/* User Info Header */}
          <Box sx={{ px: 2, py: 2, borderBottom: '1px solid rgba(13, 110, 253, 0.1)' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a202c' }}>
              {name || 'User'}
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              Session: {formatSessionTime(sessionTime)}
            </Typography>
          </Box>

         

          <MenuItem  onClick={() => {
              handleUserMenuClose();
              settingsOpen();
            }}>
            <ListItemIcon>
              <SettingsIcon sx={{ color: '#64748b' }} />
            </ListItemIcon>
            <ListItemText primary="Settings" />
          </MenuItem>

          <Divider sx={{ my: 1, borderColor: 'rgba(13, 110, 253, 0.1)' }} />

          <MenuItem 
            onClick={() => {
              handleUserMenuClose();
              logout();
            }}
            sx={{ 
              color: '#f44336',
              '&:hover': { 
                background: 'rgba(244, 67, 54, 0.08)' 
              }
            }}
          >
            <ListItemIcon>
              <LogoutIcon sx={{ color: '#f44336' }} />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </MenuItem>
        </StyledMenu>

        {/* Notifications Menu */}
        <StyledMenu
          anchorEl={notificationAnchor}
          open={Boolean(notificationAnchor)}
          onClose={handleNotificationClose}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        >
          <Box sx={{ px: 2, py: 2, borderBottom: '1px solid rgba(13, 110, 253, 0.1)' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#1a202c' }}>
              Notifications
            </Typography>
            <Typography variant="caption" sx={{ color: '#64748b' }}>
              {notifications} new notifications
            </Typography>
          </Box>

          <MenuItem onClick={handleNotificationClose}>
            <ListItemText 
              primary="New request submitted" 
              secondary="2 minutes ago"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </MenuItem>

          <MenuItem onClick={handleNotificationClose}>
            <ListItemText 
              primary="Approval required" 
              secondary="5 minutes ago"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </MenuItem>

          <MenuItem onClick={handleNotificationClose}>
            <ListItemText 
              primary="Request completed" 
              secondary="10 minutes ago"
              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </MenuItem>

          <Divider sx={{ my: 1 }} />

          <MenuItem 
            onClick={handleNotificationClose}
            sx={{ 
              justifyContent: 'center',
              color: '#0d6efd',
              fontWeight: 600
            }}
          >
            View All Notifications
          </MenuItem>
        </StyledMenu>
      </Toolbar>
    </StyledAppBar>
  );
};

export default Header;